import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { receiveMaterial } from '@/lib/api/materialOrders';
import { materialOrderSummary, parseConfirmedQuantity } from '@/lib/productionMaterialPlanning';

export function MaterialTrackingReceipt({ orderId, group, orders, onReceived }) {
  const summary = materialOrderSummary(group.needed, orders);
  const canReceive = orders.length > 0 && orders.every(o => o.confirmed_quantity != null) && summary.confirmed > 0;
  const [expanded, setExpanded] = React.useState(false);
  const [quantity, setQuantity] = React.useState(String(summary.toReceive));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const saving = React.useRef(false);
  const request = React.useRef(null);
  const inputId = React.useId();
  React.useEffect(() => { setQuantity(String(summary.toReceive)); request.current = null; }, [summary.toReceive]);
  const parsed = parseConfirmedQuantity(quantity);
  const valid = Number.isFinite(parsed) && parsed > 0;
  const receive = async event => {
    event.preventDefault();
    if (!canReceive || !valid || saving.current) return;
    saving.current = true; setBusy(true); setError(''); setMessage('');
    request.current ||= crypto.randomUUID();
    try {
      const result = await receiveMaterial(request.current, orderId, group, parsed, orders);
      await onReceived(result);
      request.current = null;
      setExpanded(false);
      setMessage('Recepción registrada.');
    } catch (e) { setError(e.message); }
    finally { saving.current = false; setBusy(false); }
  };
  if (!canReceive) return null;
  return <section className="space-y-3">
    <div className="flex justify-end"><Button type="button" className="bg-green-700 text-white hover:bg-green-800" aria-expanded={expanded} aria-controls={inputId} disabled={busy} onClick={() => { setExpanded(value => !value); setMessage(''); }}>
      {expanded ? 'Ocultar recepción' : summary.toReceive > 0 ? 'Recibir material' : 'Registrar otra recepción'}
    </Button></div>
    {expanded && <form id={inputId} onSubmit={receive} className="space-y-3 rounded border bg-gray-50 p-3">
      <p className="text-sm">Registra la cantidad recibida de {group.name} · {group.color}. Pendiente de recibir: {Number(summary.toReceive).toLocaleString('es-ES', { maximumFractionDigits: 4 })} {group.unit}.</p>
      {orders.length > 1 && <p className="text-xs text-gray-600">La recepción se reparte entre los pedidos de este material y color.</p>}
      <div className="flex flex-wrap items-end justify-end gap-3">
        <label htmlFor={`${inputId}-quantity`}>Cantidad recibida ({group.unit})<Input id={`${inputId}-quantity`} className="w-44" inputMode="decimal" disabled={busy} value={quantity} onChange={event => { request.current = null; setQuantity(event.target.value); }} /></label>
        <Button type="submit" disabled={!valid || busy}>{busy ? 'Registrando…' : 'Registrar recepción'}</Button>
      </div>
      {!valid && <p className="text-sm text-amber-800">Introduce una cantidad mayor que 0, con hasta cuatro decimales.</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </form>}
    {message && <p role="status" className="text-right text-sm text-green-700">{message}</p>}
  </section>;
}
